/**
 * W2-UX-FIX2 (Today 2.0 pixel check, 2026-10-18, defect 3): the fallback of a lazy route.
 *
 * The first visit from a Today door to Races (or Team) waited about 1.9 s on a bare "Loading races..." line in the
 * top left corner: the `Suspense` fallback in `App.tsx` was a `<div role="status">` holding text. With a 2.0 flag
 * on (`today_2_0_v1`, the surface the doors live on, or `races_2_0_v1`) the fallback is `RouteFrame` instead: the
 * page's own cream at the page's own height, the same status role, the same words as its accessible name and none
 * on screen. With both flags off the fallback is the original element, byte for byte.
 *
 * Today's race doors also start loading the Races chunk before the tap (`preloadRacesRoute`), so the frame is the
 * rare case, not the usual one.
 */
import { useServerFlag } from '../../hooks/useServerFlag';
import styles from './RouteFrame.module.css';

/** The 2.0 loading frame of a lazy route: a named status region on the page background, no visible text. */
export function RouteFrame({ label }: { label: string }) {
  return <div className={styles.frame} role="status" aria-busy="true" aria-label={label} />;
}

/**
 * The `Suspense` fallback for a lazy route. `text` is the original fallback string ("Loading races…"); flag off it
 * is printed exactly as before, flag on it becomes the frame's accessible name (without the ellipsis).
 */
export function RouteFallback({ text }: { text: string }) {
  const todayV2 = useServerFlag('today_2_0_v1');
  const racesV2 = useServerFlag('races_2_0_v1');
  if (todayV2 || racesV2) return <RouteFrame label={text.replace(/…$/, '')} />;
  return <div role="status">{text}</div>;
}
